import type { ReactNode } from "react";
import { Flex, Icon, Popover, Text } from "orcs-design-system";

import {
  EFFECT_LABEL,
  type Effect,
} from "src/pages/WorkforceGuardrails/ruleShape";

export type IconVariant =
  | "primary"
  | "secondary"
  | "success"
  | "warning"
  | "danger"
  | "grey";

/** String form is `far`. Use `fas` when that is the registered inventory prefix. */
export type SeverityIconName = string | [string, string];

export const severityOf = (
  effect: Effect | null
): { icon: SeverityIconName; variant: IconVariant; label: string } => {
  switch (effect) {
    case "BLOCK":
      return {
        icon: "circle-xmark",
        variant: "danger",
        label: EFFECT_LABEL.BLOCK,
      };
    case "WARN":
      return {
        icon: "exclamation-triangle",
        variant: "warning",
        label: EFFECT_LABEL.WARN,
      };
    case "ADD_APPROVAL_STEP":
      return {
        icon: "user-plus",
        variant: "primary",
        label: EFFECT_LABEL.ADD_APPROVAL_STEP,
      };
    case "SKIP_APPROVAL_STEP":
      return {
        icon: ["fas", "user-minus"],
        variant: "secondary",
        label: EFFECT_LABEL.SKIP_APPROVAL_STEP,
      };
    case "AUTO_APPROVE":
      return {
        icon: ["fas", "bolt"],
        variant: "success",
        label: EFFECT_LABEL.AUTO_APPROVE,
      };
    case null:
      return {
        icon: ["fas", "ban"],
        variant: "grey",
        label: "Retired",
      };
    default: {
      const unexpected: never = effect;
      return unexpected;
    }
  }
};

/**
 * Hover copy for a rule effect icon.
 * The edit-rule effect select does not use this.
 */
const effectTooltip = (effect: Effect | null): ReactNode => {
  switch (effect) {
    case "BLOCK":
      return "This prevents the requisition from proceeding entirely.";
    case "WARN":
      return "Shows a warning on the requisition and mandates an exception reason be added by the user raising in order to proceed.";
    case "ADD_APPROVAL_STEP":
      return (
        <Flex flexDirection="column" gap="xs">
          <Text weight="bold" fontSize="0">
            Add an approval step
          </Text>
          <Text fontSize="0">
            An additional approval request is added to the requisition.
          </Text>
        </Flex>
      );
    case "SKIP_APPROVAL_STEP":
      return (
        <Flex flexDirection="column" gap="xs">
          <Text weight="bold" fontSize="0">
            Remove an approval step
          </Text>
          <Text fontSize="0">
            Will skip an approval request on the requisition.
          </Text>
        </Flex>
      );
    case "AUTO_APPROVE":
      return "Use the fast-track template";
    case null:
      return "Retired";
    default: {
      const unexpected: never = effect;
      return unexpected;
    }
  }
};

export const GuardrailSeverityIcon = ({
  effect,
}: {
  effect: Effect | null;
}) => {
  const { icon, variant, label } = severityOf(effect);
  return (
    <Popover
      direction="top"
      inlineBlock
      width="280px"
      offset={{ mainAxis: 8, alignmentAxis: 0 }}
      text={effectTooltip(effect)}
    >
      <Icon
        icon={icon}
        variant={variant}
        fixedWidth
        role="img"
        aria-label={label}
      />
    </Popover>
  );
};
